import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Paperclip,
  X,
  Loader2,
  Image as ImageIcon,
  Link as LinkIcon,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
const ACCEPT_ATTR = "image/jpeg,image/png,image/webp,image/gif,image/avif,image/jpg";

function absUrl(u) {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${BACKEND}${u}`;
  return u;
}

/**
 * PhotoUploader (light theme + reliable file picker for iOS/Android/Desktop)
 *
 * Click pattern uses a real <button> that programmatically opens the hidden input.
 * The input is NOT wrapped in a label with onClick (that pattern is unreliable on iOS).
 */
export default function PhotoUploader({ value = [], onChange }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState([]);
  const [urlInput, setUrlInput] = useState("");

  // Programmatic open of native picker — works on iOS Safari, Android Chrome, desktop
  const openPicker = () => {
    if (inputRef.current) {
      try {
        inputRef.current.click();
      } catch (err) {
        console.error("Picker open failed", err);
        toast.error("Impossibile aprire il selettore file");
      }
    }
  };

  const validateFiles = (files) => {
    const valid = [];
    for (const f of files) {
      const okType =
        ALLOWED_TYPES.includes(f.type) ||
        /\.(jpe?g|png|webp|gif|avif)$/i.test(f.name || "");
      if (!okType) {
        toast.error(`Formato non supportato: ${f.name}`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`Troppo grande (max 15 MB): ${f.name}`);
        continue;
      }
      valid.push(f);
    }
    return valid;
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const valid = validateFiles(files);
    if (!valid.length) return;

    const newPending = valid.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
      name: f.name,
      size: f.size,
      progress: 0,
      status: "uploading",
    }));
    setPending((p) => [...p, ...newPending]);

    const uploaded = [];
    for (let i = 0; i < valid.length; i++) {
      const file = valid[i];
      const item = newPending[i];
      const form = new FormData();
      form.append("files", file);
      try {
        const { data } = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded * 100) / e.total);
            setPending((arr) =>
              arr.map((x) => (x.id === item.id ? { ...x, progress: pct } : x))
            );
          },
        });
        const url = data?.files?.[0]?.url;
        if (url) uploaded.push(url);
        setPending((arr) =>
          arr.map((x) =>
            x.id === item.id ? { ...x, progress: 100, status: "done" } : x
          )
        );
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err)}`);
        setPending((arr) =>
          arr.map((x) => (x.id === item.id ? { ...x, status: "error" } : x))
        );
      }
    }

    if (uploaded.length) {
      onChange([...value, ...uploaded]);
      toast.success(`${uploaded.length} foto caricat${uploaded.length === 1 ? "a" : "e"}`);
    }
    setTimeout(() => {
      setPending((arr) => arr.filter((x) => x.status === "uploading"));
    }, 1400);
  };

  const onInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = ""; // allow same file twice
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
  };

  const addByUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (!/^https?:\/\//.test(u)) {
      toast.error("URL non valido (deve iniziare con http:// o https://)");
      return;
    }
    onChange([...value, u]);
    setUrlInput("");
    toast.success("URL aggiunto");
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div data-testid="photo-uploader">
      {/* Hidden native input — must be in DOM so click() works */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        multiple
        onChange={onInputChange}
        className="sr-only"
        data-testid="photo-file-input"
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Drop zone — purely visual, the real trigger is the button inside */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="photo-dropzone"
        className={`relative block rounded-2xl border-2 border-dashed transition-all p-6 md:p-10 text-center ${
          dragOver
            ? "border-[#76BA1B] bg-[#f0fdf4]"
            : "border-slate-300 bg-slate-50 hover:border-[#76BA1B]/60 hover:bg-[#f0fdf4]/40"
        }`}
      >
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            data-testid="photo-pick-btn"
            onClick={openPicker}
            className="w-16 h-16 rounded-2xl bg-[#76BA1B] text-white flex items-center justify-center shadow-[0_10px_28px_rgba(118,186,27,0.45)] hover:bg-[#5fa015] hover:scale-105 active:scale-95 transition-all"
            aria-label="Apri selettore file"
          >
            <Paperclip size={26} strokeWidth={2.4} />
          </button>
          <div>
            <div className="font-heading text-lg font-bold text-[#1e293b]">
              Allega foto
            </div>
            <div className="text-sm text-slate-600 mt-1">
              Trascina qui i file{" "}
              <span className="hidden sm:inline">oppure</span>
              <button
                type="button"
                onClick={openPicker}
                data-testid="photo-pick-link"
                className="ml-1 text-[#76BA1B] hover:text-[#5fa015] font-bold underline-offset-2 hover:underline"
              >
                scegli dal dispositivo
              </button>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-2 font-semibold">
              JPG · PNG · WEBP · GIF · AVIF · max 15 MB
            </div>
          </div>
        </div>
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="upload-pending-list">
          {pending.map((p) => (
            <div
              key={p.id}
              className="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    p.status === "error"
                      ? "bg-red-50 text-red-500"
                      : p.status === "done"
                      ? "bg-[#f0fdf4] text-[#76BA1B]"
                      : "bg-[#f0fdf4] text-[#76BA1B]"
                  }`}
                >
                  {p.status === "done" ? (
                    <CheckCircle2 size={18} />
                  ) : p.status === "error" ? (
                    <AlertCircle size={18} />
                  ) : (
                    <ImageIcon size={18} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-[#1e293b] truncate">
                    {p.name}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {(p.size / 1024).toFixed(0)} KB · {p.progress}%
                  </div>
                </div>
                {p.status === "uploading" && (
                  <Loader2 size={14} className="animate-spin text-slate-400" />
                )}
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full transition-all duration-200 ${
                    p.status === "error" ? "bg-red-500" : "bg-[#76BA1B]"
                  }`}
                  style={{ width: `${p.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* URL fallback */}
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <LinkIcon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            data-testid="photo-url-input"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addByUrl();
              }
            }}
            placeholder="…oppure incolla un URL (Cloudinary, Imgur, ecc.)"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-[#1e293b] placeholder-slate-400 focus:border-[#76BA1B] focus:shadow-[0_0_0_4px_rgba(118,186,27,0.12)] outline-none transition"
          />
        </div>
        <button
          type="button"
          onClick={addByUrl}
          data-testid="photo-url-add"
          className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold text-[#1e293b] transition whitespace-nowrap"
        >
          Aggiungi URL
        </button>
      </div>

      {/* Thumbnails */}
      {value.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
            {value.length} foto allegate
          </div>
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
            data-testid="photo-thumbnails"
          >
            {value.map((u, i) => (
              <div
                key={`${u}-${i}`}
                className="relative group rounded-xl overflow-hidden aspect-[4/3] border border-slate-200 bg-slate-100"
              >
                <img
                  src={absUrl(u)}
                  alt={`Foto ${i + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-md bg-white/95 text-[10px] font-bold text-[#1e293b] shadow">
                  {i + 1}
                </div>
                <button
                  type="button"
                  data-testid={`photo-remove-${i}`}
                  onClick={() => removeAt(i)}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition shadow-lg"
                  aria-label="Rimuovi"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
